/**
 * Merci : le prix du bouton d'achat multiplié par la quantité choisie.
 * Données dans `snippets/merci-prix-quantite.liquid`, posées sur le prix du
 * bouton de la fiche et sur celui de la barre collante.
 *
 * **La quantité se lit dans le formulaire du produit**, pas dans l'événement :
 * c'est elle qu'ajoutent les deux boutons — celui de la barre collante ne fait
 * que déclencher l'autre. Une seule source, donc, quel que soit le déclencheur :
 * les boutons « − » et « + » (`quantity-selector:update`), la frappe dans le
 * champ (`input`), ou la reconstruction du bouton au changement de variante.
 *
 * **La reconstruction remet le prix unitaire.** Horizon refait le bouton, et la
 * barre collante, à chaque changement de variante : un `MutationObserver`
 * repère le nouveau prix et lui réapplique la quantité. Chaque prix garde la
 * quantité déjà appliquée (`data-merci-quantite`), ce qui évite de réécrire —
 * et de se relancer soi-même — quand rien n'a changé.
 *
 * Seuls les nombres sont réécrits : le texte masqué qui précède le prix soldé
 * et le prix barré (« Prix soldé », « Prix normal ») reste en place.
 */
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const SELECTEUR = '[data-merci-prix-quantite]';

/**
 * La quantité du formulaire du produit, 1 si elle est vide ou invalide.
 * @param {string | undefined} productId
 * @returns {number}
 */
function quantite(productId) {
  if (!productId) return 1;
  const champ = document.querySelector(
    `product-form-component[data-product-id="${productId}"] input[name="quantity"]`
  );
  const valeur = parseInt(champ instanceof HTMLInputElement ? champ.value : '', 10);
  return Number.isFinite(valeur) && valeur > 0 ? valeur : 1;
}

/**
 * Remplace le texte d'un élément de prix, sans toucher à ses enfants.
 * @param {Element | null} element
 * @param {string} texte
 */
function ecrire(element, texte) {
  if (!element) return;
  for (const noeud of [...element.childNodes]) {
    if (noeud.nodeType === Node.TEXT_NODE) noeud.remove();
  }
  element.append(texte);
}

/** @param {HTMLElement} prix */
function appliquer(prix) {
  const q = quantite(prix.dataset.productId);
  if (prix.dataset.merciQuantite === String(q)) return;

  const format = prix.dataset.moneyFormat || '{{amount}}';
  const devise = prix.dataset.currency || 'EUR';
  const unitaire = Number(prix.dataset.prix) || 0;
  const barre = Number(prix.dataset.prixBarre) || 0;

  prix.dataset.merciQuantite = String(q);
  ecrire(prix.querySelector('.price'), formatMoney(unitaire * q, format, devise));
  if (barre > 0) {
    ecrire(prix.querySelector('.compare-at-price'), formatMoney(barre * q, format, devise));
  }
}

function toutAppliquer() {
  for (const prix of document.querySelectorAll(SELECTEUR)) {
    if (prix instanceof HTMLElement) appliquer(prix);
  }
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  // Une ligne du panier a aussi son sélecteur : elle n'a rien à voir ici.
  if (/** @type {CustomEvent} */ (event).detail?.cartLine) return;
  toutAppliquer();
});

document.addEventListener('input', (event) => {
  const cible = event.target;
  if (cible instanceof HTMLInputElement && cible.name === 'quantity' && cible.closest('product-form-component')) {
    toutAppliquer();
  }
});

new MutationObserver(toutAppliquer).observe(document.body, { childList: true, subtree: true });
toutAppliquer();
